/**
 * Cognito Admin User Pool Configuration
 *
 * These values should be updated after deploying the 07_cognito_admin Terraform module.
 * Run: terraform output admin_portal_config
 */

// Environment-specific configuration
const configs = {
  local: {
    region: 'us-east-1',
    userPoolId: 'us-east-1_jAbTigDi8',
    clientId: '347imk39sr11gat79mtpqtb1vj',
    domain: 'tark-ue1-admin-local',
    identityProvider: 'AWSSSO',
    redirectUri: 'http://localhost:3002/callback',
    logoutUri: 'http://localhost:3002/',
  },
  dev: {
    region: 'us-east-1',
    userPoolId: '', // Fill after terraform apply
    clientId: '',   // Fill after terraform apply
    domain: '',     // e.g., tark-ue1-admin-dev
    identityProvider: 'AWSSSO',
    redirectUri: 'https://admin-dev.playtark.com/callback',
    logoutUri: 'https://admin-dev.playtark.com/',
  },
  prod: {
    region: 'us-east-1',
    userPoolId: '', // Fill after terraform apply
    clientId: '',   // Fill after terraform apply
    domain: '',     // e.g., tark-ue1-admin-prod
    identityProvider: 'AWSSSO',
    redirectUri: 'https://admin.playtark.com/callback',
    logoutUri: 'https://admin.playtark.com/',
  },
};

// Detect environment based on hostname
export function getEnvironment(): 'local' | 'dev' | 'prod' {
  if (typeof window === 'undefined') return 'local';

  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'local';
  }
  if (hostname.includes('admin-dev')) {
    return 'dev';
  }
  return 'prod';
}

export const cognitoConfig = configs[getEnvironment()];

/**
 * Check if Cognito SSO is configured
 */
export function isCognitoConfigured(): boolean {
  return !!(cognitoConfig.userPoolId && cognitoConfig.clientId && cognitoConfig.domain);
}

/**
 * Get the Cognito hosted UI base URL
 */
export function getHostedUiBaseUrl(): string {
  return `https://${cognitoConfig.domain}.auth.${cognitoConfig.region}.amazoncognito.com`;
}

/**
 * Get the OAuth2 authorize URL (direct SSO login)
 */
export function getAuthorizeUrl(): string {
  const baseUrl = getHostedUiBaseUrl();
  const params = new URLSearchParams({
    client_id: cognitoConfig.clientId,
    response_type: 'code',
    scope: 'email openid profile',
    redirect_uri: cognitoConfig.redirectUri,
    identity_provider: cognitoConfig.identityProvider,
  });
  return `${baseUrl}/oauth2/authorize?${params.toString()}`;
}

/**
 * Get the token endpoint URL
 */
export function getTokenEndpoint(): string {
  return `${getHostedUiBaseUrl()}/oauth2/token`;
}

/**
 * Get the logout URL
 */
export function getLogoutUrl(): string {
  const baseUrl = getHostedUiBaseUrl();
  const params = new URLSearchParams({
    client_id: cognitoConfig.clientId,
    logout_uri: cognitoConfig.logoutUri,
  });
  return `${baseUrl}/logout?${params.toString()}`;
}

/**
 * Exchange authorization code for tokens
 */
export async function exchangeCodeForTokens(code: string): Promise<{
  access_token: string;
  id_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
}> {
  const response = await fetch(getTokenEndpoint(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: cognitoConfig.clientId,
      code,
      redirect_uri: cognitoConfig.redirectUri,
    }).toString(),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Token exchange failed: ${error}`);
  }

  return response.json();
}

/**
 * Refresh tokens using refresh token
 */
export async function refreshTokens(refreshToken: string): Promise<{
  access_token: string;
  id_token: string;
  expires_in: number;
  token_type: string;
}> {
  const response = await fetch(getTokenEndpoint(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: cognitoConfig.clientId,
      refresh_token: refreshToken,
    }).toString(),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Token refresh failed: ${error}`);
  }

  return response.json();
}

/**
 * Parse JWT token to get claims (without verification - server will verify)
 */
export function parseJwt(token: string): Record<string, unknown> {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return {};
  }
}

/**
 * Check if token is expired
 */
export function isTokenExpired(token: string): boolean {
  const claims = parseJwt(token);
  const exp = claims.exp as number | undefined;
  if (!exp) return true;
  return Date.now() >= exp * 1000;
}
